/**
 * Query-only Swup visits — result-card cascade.
 *
 * A "query-only" visit is a same-path navigation whose ONLY change is the query
 * string: filter bars, sort dropdowns, pill add/remove. Reloading the whole
 * viewport (a root view transition + scroll-to-top) for what is really an
 * in-place list update is jarring — the surrounding chrome and the filter bar
 * itself flash and the reader loses their scroll position.
 *
 * For these visits this module:
 *   1. flags the visit (`visit.isQueryOnly`) and, at `visit:start`, suppresses
 *      the whole-viewport native view transition (`visit.animation.native =
 *      false`) and the scroll reset (`visit.scroll.reset = false`) — while
 *      KEEPING `visit.animation.animate = true` so the out/in await hooks still
 *      fire (see the Swup transition flow in router/swup.js);
 *   2. fades ONLY the result cards out — a promise Swup AWAITS before
 *      `content:replace`, so the old cards are gone before the DOM swaps;
 *   3. staggers the NEW cards in after the swap, with the total stagger spread
 *      CAPPED so a large result set doesn't animate for seconds.
 *
 * Everything animates on the compositor thread only (`opacity` + `transform`),
 * never layout properties, and no layout is read. GSAP is deferred (idle-loaded)
 * so every step is a graceful no-op until `window.gsap` exists — the content
 * still swaps, just without the cascade.
 *
 * INFRASTRUCTURE PREREQUISITE — CDN cache key MUST include the query string.
 * These visits are distinct pages to the origin (`/list?tag=a` ≠ `/list?tag=b`).
 * A CDN or static cache that strips/ignores the query string in its cache key
 * will serve the unfiltered page for every filter, and the cascade will animate
 * identical cards back in. Whitelist the filter params in the cache key (or, for
 * Statamic static caching, exclude the listing route). See
 * documents/reference/page-builder.md ("Query-only visits & the CDN cache key").
 *
 * @module transitions/query-cascade
 */

/**
 * @typedef {Object} QueryCascadeConfig
 * @property {string} gridSelector  Container holding the result cards.
 * @property {string} cardSelector  Individual result card (queried within the grid).
 * @property {number} outDuration   Fade-out duration (seconds).
 * @property {number} inDuration    Per-card fade-in duration (seconds).
 * @property {number} inStagger     Per-card stagger step (seconds).
 * @property {number} inStaggerCap  Maximum TOTAL stagger spread (seconds) — the cap.
 * @property {number} inShift       Compositor-only entry translate distance (px, on Y).
 */

/** Sensible kit defaults. Projects override the selectors to match their listing. */
export const DEFAULTS = {
    gridSelector: '[data-results-grid]',
    cardSelector: '[data-results-card]',
    outDuration: 0.2,
    inDuration: 0.35,
    inStagger: 0.05,
    inStaggerCap: 0.4,
    inShift: 8,
};

/**
 * Split a Swup visit URL (path + search, relative or absolute) into its path
 * and search components, resolved against the current origin.
 *
 * @param {string} url
 * @returns {{ path: string, search: string }}
 */
function splitUrl(url) {
    const resolved = new URL(url, window.location.origin);
    return { path: resolved.pathname, search: resolved.search };
}

/**
 * A visit is query-only when the path is unchanged and ONLY the query string
 * differs — adding, changing, or clearing filter params.
 *
 * @param {(string | undefined)} fromUrl - `visit.from.url`
 * @param {(string | undefined)} toUrl - `visit.to.url`
 * @returns {boolean}
 */
export function isQueryOnlyNavigation(fromUrl, toUrl) {
    if (!fromUrl || !toUrl) {
        return false;
    }

    const from = splitUrl(fromUrl);
    const to = splitUrl(toUrl);

    return from.path === to.path && from.search !== to.search;
}

/**
 * Collect the result cards inside the grid, in DOM order.
 *
 * @param {QueryCascadeConfig} config
 * @returns {HTMLElement[]}
 */
function resultCards(config) {
    const grid = document.querySelector(config.gridSelector);

    if (!grid) {
        return [];
    }

    return Array.from(grid.querySelectorAll(config.cardSelector));
}

/**
 * Is the viewer's reduced-motion preference set? Feature-detected each call so a
 * mid-session OS toggle is honoured.
 *
 * @returns {boolean}
 */
function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Fade the current result cards out. Returned promise is awaited by Swup before
 * the content swap, so the outgoing cards clear before the incoming ones render.
 * No-op (resolves immediately) without GSAP, without cards, or under reduced
 * motion.
 *
 * @param {QueryCascadeConfig} config
 * @returns {Promise<void>}
 */
function fadeCardsOut(config) {
    const gsap = window.gsap;
    const cards = resultCards(config);

    if (!gsap || !cards.length || prefersReducedMotion()) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        gsap.to(cards, {
            opacity: 0,
            duration: config.outDuration,
            ease: 'power2.in',
            onComplete: resolve,
        });
    });
}

/**
 * Stagger the freshly-swapped result cards in, with the total stagger spread
 * capped at `inStaggerCap` so large result sets stay snappy. Compositor-only
 * (opacity + Y translate). No-op without GSAP/cards; instant under reduced motion.
 *
 * @param {QueryCascadeConfig} config
 * @returns {Promise<void>}
 */
function staggerCardsIn(config) {
    const gsap = window.gsap;
    const cards = resultCards(config);

    if (!gsap || !cards.length) {
        return Promise.resolve();
    }

    // Reduced motion: reveal instantly, no stagger, no drift.
    if (prefersReducedMotion()) {
        gsap.set(cards, { clearProps: 'opacity,transform' });
        return Promise.resolve();
    }

    const spread = Math.min(cards.length * config.inStagger, config.inStaggerCap);

    return new Promise((resolve) => {
        gsap.fromTo(
            cards,
            { opacity: 0, y: config.inShift },
            {
                opacity: 1,
                y: 0,
                duration: config.inDuration,
                ease: 'power2.out',
                stagger: { amount: spread, from: 'start' },
                clearProps: 'opacity,transform',
                onComplete: resolve,
            },
        );
    });
}

/**
 * Wire the query-only cascade onto a Swup instance.
 *
 * Registers three hooks: a `visit:start` classifier that flags query-only
 * visits and suppresses their whole-viewport transition + scroll reset, an
 * `animation:out:await` fade-out (awaited before the swap), and an
 * `animation:in:await` capped stagger-in (after the swap).
 *
 * @param {import('swup').default} swup - The Swup instance.
 * @param {Partial<QueryCascadeConfig>} [options] - Selector/timing overrides.
 * @returns {void}
 */
export function initQueryCascade(swup, options = {}) {
    const config = { ...DEFAULTS, ...options };

    swup.hooks.on('visit:start', (visit) => {
        if (!isQueryOnlyNavigation(visit.from?.url, visit.to?.url)) {
            return;
        }

        visit.isQueryOnly = true;
        // Suppress the whole-viewport native view transition — we animate only
        // the cards. `animate` stays true so the out/in await hooks still fire.
        visit.animation.native = false;
        // Keep the reader where they are; a filter is an in-place update.
        visit.scroll.reset = false;
    });

    swup.hooks.on('animation:out:await', (visit) => {
        if (!visit.isQueryOnly) {
            return undefined;
        }

        return fadeCardsOut(config);
    });

    swup.hooks.on('animation:in:await', (visit) => {
        if (!visit.isQueryOnly) {
            return undefined;
        }

        return staggerCardsIn(config);
    });
}
